import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Field, reduxForm, change, formValueSelector,
} from 'redux-form';
import { connect } from 'react-redux';
import SelectWrapper from '../../../../user/components/user/body/select_wrapper/SelectWrapper';
import InputWrapper from '../../../../user/components/user/body/input_wrapper/InputWrapper';
import TextAreaWrapper from '../../../../product/components/reviews_section/submit_review_form/redux_form_wrappers/TextAreaWrapper';
import styles from './AboutForm.module.sass';
import validations from '../../../../services/validations';
import { userObjectSelector } from '../../../../user/selectors/user';
import ClickableDiv from '../../../../ui_kit/clickable_div/ClickableDiv';
import attachIcon from '../../../../../images/pages/quiz/attach.svg';
import clearIcon from '../../../../../images/pages/quiz/clear.svg';
import SKIN_TYPES from '../../../constants/skin_types';
import COUNTRIES, { STATES_BY_COUNTRY } from '../../../constants/countries';

function AboutForm(props) {
  const {
    files, setFiles, concerns, solvingProblems,
    setFieldValue, selectedCountry,
  } = props;

  const fileInputRef = useRef(null);
  const onFileSelected = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const filesInInput = event.target.files;

    if (filesInInput.length === 0) { return; }

    const lookup = {};

    const newFiles = [...files, ...filesInInput].filter(
      (file) => {
        if (!lookup[file.name]) {
          lookup[file.name] = true;
          return true;
        }
        return false;
      },
    );

    setFiles(newFiles);

    fileInputRef.current.value = '';
  };

  const onFileRemove = (fileToRemove) => {
    const newFiles = files.filter((file) => file.name !== fileToRemove.name);
    setFiles(newFiles);
  };

  const availableStates = () => STATES_BY_COUNTRY[selectedCountry];

  useEffect(() => {
    setFieldValue('solvingProblems', concerns.join(', ') || solvingProblems);
  }, []);

  useEffect(() => {
    if (!availableStates()) { return; }

    const [firstAvailableState] = availableStates();

    setFieldValue('state', firstAvailableState);
  }, [selectedCountry]);

  return (
    <form className={styles.form}>
      <div className={styles.column}>
        <div className={styles.row}>
          <div className={styles.halfsize}>
            <Field
              name="firstName"
              component={InputWrapper}
              label="First Name *"
              type="text"
              className={styles.input}
            />
          </div>
          <div className={styles.halfsize}>
            <Field
              name="lastName"
              component={InputWrapper}
              label="Last Name *"
              type="text"
              className={styles.input}
            />
          </div>
        </div>
        <Field
          name="country"
          component={SelectWrapper}
          options={
            Object.values(COUNTRIES).map((country) => ({ label: country, value: country }))
          }
          label="Country *"
          className={styles.input}
        />
        { availableStates() && (
          <Field
            name="state"
            component={SelectWrapper}
            label="State *"
            className={styles.input}
            options={
              availableStates().map((state) => ({ label: state, value: state }))
            }
          />
        )}
        <div className={styles.halfsize}>
          <Field
            name="age"
            component={InputWrapper}
            type="text"
            label="Your age"
            normalize={(val, prevVal) => {
              if (val) {
                return (/^\d+$/.test(val)) ? String(parseInt(val, 10)) : prevVal;
              }
              return val;
            }}
            inputmode="numeric"
            className={styles.input}
          />
        </div>
        <Field
          name="instagramLink"
          component={InputWrapper}
          type="text"
          label="Instagram link"
          className={styles.input}
        />
        <Field
          name="facebookLink"
          component={InputWrapper}
          type="text"
          label="Facebook link"
          className={styles.input}
        />
      </div>
      <div className={styles.column}>
        <Field
          name="skinType"
          component={SelectWrapper}
          options={
            SKIN_TYPES.map((skinType) => ({ label: skinType, value: skinType }))
          }
          label="Skin Type *"
          className={styles.input}
        />
        { concerns.length === 0 && (
          <Field
            name="solvingProblems"
            component={InputWrapper}
            type="text"
            label="What beauty problem can we help you solve? *"
            className={styles.input}
          />
        )}
        { concerns.length > 0 && (
          <Field
            name="howHearAboutUs"
            component={InputWrapper}
            type="text"
            label="How did you hear about us?"
            className={styles.input}
          />
        )}
        <Field
          name="inspiration"
          component={TextAreaWrapper}
          type="text"
          label="Tell us your inspiration for this product *"
          className={styles.textarea}
        />
        <Field
          name="anythingElse"
          component={InputWrapper}
          type="text"
          label="Anything else you'd like us to know"
          className={styles.input}
        />
        <div className={styles.label}>
          Attached files
        </div>
        <div className={styles.files}>
          {files.map((file) => (
            <div className={styles.file} key={file.name}>
              {file.name}
              <ClickableDiv onClick={() => { onFileRemove(file); }}>
                <img src={clearIcon} alt="remove" />
              </ClickableDiv>
            </div>
          ))}
        </div>
        <ClickableDiv className={styles['choose-file']} onClick={() => { fileInputRef.current.click(); }}>
          <img src={attachIcon} alt="choose file" />
          Choose file
        </ClickableDiv>
        <input
          type="file"
          ref={fileInputRef}
          multiple
          onChange={onFileSelected}
        />
      </div>
    </form>
  );
}

AboutForm.propTypes = {
  initialValues: PropTypes.shape({}),
  files: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setFiles: PropTypes.func.isRequired,
  concerns: PropTypes.arrayOf(PropTypes.string).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  solvingProblems: PropTypes.string.isRequired,
  selectedCountry: PropTypes.string,
};

AboutForm.defaultProps = {
  initialValues: null,
  selectedCountry: null,
};

const AboutFormWithForm = reduxForm({
  form: 'about',
  enableReinitialize: true,
  validate: validations.aboutForm,
})(AboutForm);

const mapStateToProps = (state) => ({
  user: userObjectSelector(state),
  selectedCountry: formValueSelector('about')(state, 'country'),
});

const mapDispatchToProps = (dispatch) => ({
  setFieldValue: (field, value) => dispatch(change('about', field, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AboutFormWithForm);
